import Prose from './Prose';
import type { Announcement as AnnouncementData } from '../lib/api';

/**
 * Full-width bar sitting directly beneath the navigation. Single always-on
 * block — staff edit the title, date, time and venue in the admin area, and can
 * switch the whole bar off there.
 */
export default function Announcement({ data }: { data: AnnouncementData | undefined }) {
  if (!data?.isActive) return null;

  const details = [
    { label: 'Date', value: data.date },
    { label: 'Time', value: data.time },
    { label: 'Venue', value: data.venue },
  ].filter((d) => d.value?.trim());

  // Nothing worth showing a bar for.
  if (!data.title?.trim() && details.length === 0 && !data.bodyHtml?.trim()) return null;

  return (
    <section
      aria-labelledby="announcement-heading"
      className="border-b border-gold-200 bg-linear-to-r from-gold-100 via-gold-100/80 to-sand-100"
    >
      <div className="mx-auto flex w-full items-start gap-3.5 px-4 py-3.5 sm:px-6 lg:px-10 xl:px-14">
        {/* Megaphone reads as "notice" far better than a warning triangle. */}
        <span
          aria-hidden
          className="mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-full bg-gold-400/25 text-gold-600 ring-1 ring-gold-300/50"
        >
          <svg viewBox="0 0 24 24" className="h-4.5 w-4.5" fill="currentColor">
            <path d="M18.5 3a1 1 0 0 0-1.07.14L11.3 8.2H7a3 3 0 0 0-3 3v1.6a3 3 0 0 0 3 3h.6l1.1 4.35a1 1 0 0 0 .97.75h1.8a1 1 0 0 0 .96-1.28l-1.05-3.82h.92l6.13 5.06A1 1 0 0 0 19 20.1V3.9a1 1 0 0 0-.5-.9Z" />
            <path
              d="M20.9 8.3a1 1 0 0 1 1.32.5 5.6 5.6 0 0 1 0 4.4 1 1 0 1 1-1.82-.82 3.6 3.6 0 0 0 0-2.76 1 1 0 0 1 .5-1.32Z"
              opacity=".65"
            />
          </svg>
        </span>

        <div className="min-w-0">
          {data.title?.trim() && (
            <h2
              id="announcement-heading"
              className="font-display text-lg font-bold leading-snug text-ink-900"
            >
              {data.title}
            </h2>
          )}

          {details.length > 0 && (
            <dl className="mt-1 flex flex-wrap items-baseline gap-x-5 gap-y-1 text-lg text-ink-900">
              {details.map((d) => (
                <div key={d.label} className="flex items-baseline gap-1.5">
                  <dt className="font-bold text-gold-700">
                    {d.label}
                  </dt>
                  <dd className="whitespace-nowrap">{d.value}</dd>
                </div>
              ))}
            </dl>
          )}

          {data.bodyHtml?.trim() && (
            <Prose
              html={data.bodyHtml}
              className="mt-1 text-lg prose-p:my-0 prose-p:text-ink-900 prose-a:text-gold-600"
            />
          )}
        </div>
      </div>
    </section>
  );
}
